// src/pages/About.jsx
import React from 'react';

const About = () => {
  const handleContactClick = () => {
    window.open('https://calendly.com/ayoub-abdelouahabi/meeting', '_blank');
  };

  const handleResumeDownload = () => {
    window.open('/resume.pdf', '_blank');
  };

  const handleLinkedInClick = () => {
    window.open('https://linkedin.com/in/ayoubabdel', '_blank');
  };

  return (
    <section className="full-section">
      <div className="about-container">
        <div className="about-header">
          <div className="about-profile">
            <div className="profile-photo">
              <img 
                src="/images/me.jpg" 
                alt="Ayoub Abdelouahabi"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="photo-placeholder">
                <span className="placeholder-initials">AM</span>
              </div>
            </div>
            <div className="profile-intro">
              <h1>Ayoub Abdelouahabi</h1>
              <p className="profile-title">Solutions Architect & Technical Product Leader</p>
              <p className="profile-location">📍 Paris, France</p>
              
              <div className="profile-stats">
                <div className="profile-stat">
                  <div className="stat-number">8+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
                <div className="profile-stat">
                  <div className="stat-number">15+</div>
                  <div className="stat-label">Projects</div>
                </div>
                <div className="profile-stat">
                  <div className="stat-number">4</div>
                  <div className="stat-label">Companies</div>
                </div>
              </div>
              
              <p className="profile-bio">
                French engineering graduate (UTBM) turned solutions architect. I help companies 
                bridge technical execution with strategic vision, from enterprise systems to 
                data platforms.
              </p>
              
              <div className="profile-links">
                <button 
                  className="btn-primary" 
                  onClick={handleContactClick}
                >
                  Schedule Call
                </button>
                <button 
                  className="btn-secondary"
                  onClick={handleLinkedInClick}
                >
                  LinkedIn
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="about-content">
          <div className="about-main">
            {/* My Journey Section - Updated */}
<div className="section-block">
  <h2>My Journey</h2>
  
  <div className="journey-narrative">
    {/* Present */}
    <div className="narrative-section present-section">
      <div className="narrative-marker">
        <span className="narrative-label">Present</span>
        <div className="narrative-line"></div>
      </div>
      <div className="narrative-content">
        <p>
          As an <strong>independent consultant</strong>, I specialize in designing end-to-end 
          SaaS solutions and scalable data architectures. My work bridges technical 
          implementation with strategic business outcomes, helping companies transform 
          their technical capabilities into competitive advantages.
        </p>
      </div>
    </div>

    {/* Recent Experience */}
    <div className="narrative-section recent-section">
      <div className="narrative-marker">
        <span className="narrative-label">Recent</span>
        <div className="narrative-line"></div>
      </div>
      <div className="narrative-content">
        <p>
          Before consulting, I spent nearly a year at <strong>Workday</strong> developing 
          cloud-native features for their Payroll SaaS module. Working within microservices 
          architecture and Agile squads, I focused on delivering enterprise-grade solutions 
          that balance innovation with reliability.
        </p>
      </div>
    </div>

    {/* Mid-Career Experience */}
    <div className="narrative-section mid-section">
      <div className="narrative-marker">
        <span className="narrative-label">Previous</span>
        <div className="narrative-line"></div>
      </div>
      <div className="narrative-content">
        <p>
          I spent nearly six years at <strong>Dassault Systèmes</strong> building Identity 
          and Access Management systems for their enterprise SaaS platform. This role 
          deepened my understanding of security, scalability, and the operational challenges 
          of serving large user bases with high availability requirements.
        </p>
      </div>
    </div>

    {/* Early Career */}
    <div className="narrative-section early-section">
      <div className="narrative-marker">
        <span className="narrative-label">Early Career</span>
        <div className="narrative-line"></div>
      </div>
      <div className="narrative-content">
        <p>
          My career began at <strong>Orange Business Services</strong>, where I developed 
          automated vulnerability management systems. This foundation in data engineering 
          and process automation established the technical rigor that continues to inform 
          my architectural approach today.
        </p>
      </div>
    </div>

    {/* Education & Foundation */}
    <div className="narrative-section foundation-section">
      <div className="narrative-marker">
        <span className="narrative-label">Foundation</span>
        <div className="narrative-line"></div>
      </div>
      <div className="narrative-content">
        <p>
          I graduated with an engineering degree from <strong>Université de Technologie 
          de Belfort-Montbéliard (UTBM)</strong>, a leading French engineering school. 
          This education provided a strong foundation in both theoretical computer science 
          and practical software engineering principles.
        </p>
      </div>
    </div>

    {/* Evolution Summary */}
    <div className="evolution-summary">
      <h3>Evolution & Direction</h3>
      <p>
        My career has evolved from <strong>technical implementation</strong> to 
        <strong> architectural strategy</strong> and <strong>technical leadership</strong>. 
        This progression has given me a unique perspective—I understand both the 
        granular details of implementation and the strategic implications of 
        architectural decisions.
      </p>
      
      <div className="evolution-highlights">
        <div className="highlight-column">
          <h4>Transformation</h4>
          <p>Guiding teams through architectural transitions and modernization</p>
        </div>
        <div className="highlight-column">
          <h4>Strategy</h4>
          <p>Aligning technical architecture with business objectives</p>
        </div>
        <div className="highlight-column">
          <h4>Execution</h4>
          <p>Balancing innovation with operational excellence</p>
        </div>
      </div>
    </div>

    {/* Value Proposition */}
    <div className="value-proposition">
      <h3>How I Help Organizations</h3>
      <div className="proposition-grid">
        <div className="proposition-card">
          <h4>Architectural Guidance</h4>
          <p>Designing scalable, maintainable systems that grow with your business</p>
        </div>
        <div className="proposition-card">
          <h4>Technical Strategy</h4>
          <p>Developing roadmaps that balance short-term delivery with long-term vision</p>
        </div>
        <div className="proposition-card">
          <h4>Team Enablement</h4>
          <p>Building capabilities and processes that empower engineering teams</p>
        </div>
      </div>
    </div>
  </div>

  <div className="journey-cta">
    <p>
      <strong>Interested in discussing how my experience can help with your technical challenges?</strong><br />
      I offer complimentary discovery sessions to explore potential collaboration.
    </p>
    <button className="btn-primary" onClick={handleContactClick}>
      Schedule a Conversation
    </button>
  </div>
</div>
            {/* Core Expertise Section */}
            <div className="section-block">
              <h3>Core Expertise</h3>
              <div className="expertise-grid">
                <div className="expertise-card">
                  <div className="expertise-header">
                    <div className="expertise-icon">☁️</div>
                    <h4>Cloud & System Architecture</h4>
                  </div>
                  <ul>
                    <li>Cloud-Native Design (AWS, Azure)</li>
                    <li>Microservices & Serverless</li>
                    <li>API Design & Integration</li>
                    <li>System Scalability & Performance</li>
                    <li>Security & Compliance</li>
                  </ul>
                </div>
                <div className="expertise-card">
                  <div className="expertise-header">
                    <div className="expertise-icon">📊</div>
                    <h4>Data Engineering & Analytics</h4>
                  </div>
                  <ul>
                    <li>Data Modeling & Warehousing</li>
                    <li>ETL/ELT Pipeline Design</li>
                    <li>Real-time Data Processing</li>
                    <li>Analytics Platform Architecture</li>
                    <li>Data Governance & Quality</li>
                  </ul>
                </div>
                <div className="expertise-card">
                  <div className="expertise-header">
                    <div className="expertise-icon">🚀</div>
                    <h4>Product & Technical Leadership</h4>
                  </div>
                  <ul>
                    <li>Technical Roadmapping</li>
                    <li>Product Strategy & Vision</li>
                    <li>Stakeholder Management</li>
                    <li>Agile & DevOps Practices</li>
                    <li>Team Mentoring & Growth</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Technology Stack */}
            <div className="section-block">
              <h3>Technology Stack</h3>
              <div className="tech-stack">
                <div className="tech-category">
                  <h4>Cloud & Infrastructure</h4>
                  <div className="tech-tags">
                    <span className="tech-tag">AWS (EC2, S3, Lambda, Glue)</span>
                    <span className="tech-tag">Docker</span>
                    <span className="tech-tag">CI/CD</span>
                    <span className="tech-tag">Kubernetes</span>
                    <span className="tech-tag">Terraform</span>
                  </div>
                </div>
                <div className="tech-category">
                  <h4>Backend & Data</h4>
                  <div className="tech-tags">
                    <span className="tech-tag">Python</span>
                    <span className="tech-tag">Java/Spring</span>
                    <span className="tech-tag">Node.js</span>
                    <span className="tech-tag">SQL/NoSQL</span>
                    <span className="tech-tag">Apache Airflow</span>
                  </div>
                </div>
                <div className="tech-category">
                  <h4>Tools & Methods</h4>
                  <div className="tech-tags">
                    <span className="tech-tag">Git/GitHub</span>
                    <span className="tech-tag">Agile/Scrum</span>
                    <span className="tech-tag">TDD/BDD</span>
                    <span className="tech-tag">UML</span>
                    <span className="tech-tag">System Design</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="about-sidebar">
            {/* Experience Timeline - Updated with current role */}
            <div className="sidebar-card">
              <h3>Experience Timeline</h3>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-date">2023 – Present</div>
                    <h4>Independent Consultant</h4>
                    <p className="company">Clamart, France</p>
                    <p className="role-desc">Data & SaaS platforms consultant, designing end-to-end solutions and scalable data architectures</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-date">2022 – 2023</div>
                    <h4>Senior Software Engineer</h4>
                    <p className="company">Workday • Paris</p>
                    <p className="role-desc">Cloud-native payroll features in microservices architecture</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-date">2016 – 2022</div>
                    <h4>R&D Software Engineer</h4>
                    <p className="company">Dassault Systèmes • Paris</p>
                    <p className="role-desc">Enterprise IAM systems for large-scale SaaS platform</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <div className="timeline-date">2016</div>
                    <h4>Software Engineer</h4>
                    <p className="company">Orange Business Services • Rennes</p>
                    <p className="role-desc">Vulnerability management & ITIL systems</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Education */}
            <div className="sidebar-card">
              <h3>Education</h3>
              <div className="education-item">
                <h4>Engineering Degree</h4>
                <p className="institution">Université de Technologie de Belfort-Montbéliard (UTBM)</p>
                <p className="year">2013 – 2016</p>
                <p className="education-desc">French engineering school specializing in computer science and software engineering</p>
              </div>
              <div className="education-item">
                <h4>Bachelor's in Software Engineering</h4>
                <p className="institution">Ecole supérieure de Technologie</p>
                <p className="year">2011 – 2013</p>
              </div>
            </div>
            
            {/* Languages */}
            <div className="sidebar-card">
              <h3>Languages</h3>
              <div className="languages">
                <div className="language-item">
                  <span className="language-name">French</span>
                  <span className="language-level">Fluent</span>
                </div>
                <div className="language-item">
                  <span className="language-name">English</span>
                  <span className="language-level">Fluent</span>
                </div>
                <div className="language-item">
                  <span className="language-name">Arabic</span>
                  <span className="language-level">Native</span>
                </div>
              </div>
            </div>
            
            {/* Certifications */}
            <div className="sidebar-card">
              <h3>Certifications & Learning</h3>
              <div className="certifications">
                <div className="certification-item">
                  <div className="cert-icon">🎯</div>
                  <div>
                    <h4>AWS Solutions Architect</h4>
                    <p>In Progress</p>
                  </div>
                </div>
                <div className="certification-item">
                  <div className="cert-icon">📊</div>
                  <div>
                    <h4>Data Engineering Specialization</h4>
                    <p>Google Cloud / Coursera</p>
                  </div>
                </div>
                <div className="certification-item">
                  <div className="cert-icon">🏗️</div>
                  <div>
                    <h4>Cloud Architecture Patterns</h4>
                    <p>Ongoing Learning</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Card */}
            <div className="sidebar-card contact-card">
              <h3>Get in Touch</h3>
              <p>Interested in discussing your project or technical challenges?</p>
              <button 
                className="btn-primary full-width"
                onClick={handleContactClick}
              >
                Book Discovery Call
              </button>
              <p className="contact-note">
                I offer free 30-minute consultations to discuss your needs and explore potential collaboration.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;